const wordSeparators = /[-_\\.+\s]+/g,
  notAlphaNumericOrSpace = /[^ a-zA-Z0-9]+/g,
  capitalizedFirstLetter = /[A-Z]+(?![a-z])|[A-Z]/g;

/**
 * Converts a string to uppercase, preserving its type.
 *
 * @typeParam
 * @param str
 * @returns The uppercase version of the input string, typed as `Uppercase<T>`.
 */
export const toUpperCase = <T extends string>(str: T) => str.toUpperCase() as Uppercase<T>;

/**
 * Converts a string to capitalize, preserving its type.
 *
 * @typeParam
 * @param str
 * @returns The capitalize version of the input string, typed as `Capitalize<T>`.
 */
export const toCapitalize = <T extends string>(str: T): Capitalize<T> => {
  if (str === "") return str as Capitalize<T>;

  const firstChar = str[0].toUpperCase();
  const rest = str.slice(1);

  return (firstChar + rest) as Capitalize<T>;
};

/**
 * Returns a menu id string, preserving its type.
 *
 * @typeParam P The prefix
 * @typeParam T The title
 * @param prefix
 * @param title
 * @returns The menu id string in the format `${P}-${T}`.
 */
export const generateId = <P extends string, T extends string>(prefix: P, title: T): `${P}-${T}` =>
  `${prefix}-${title}` as `${P}-${T}`;

/**
 * Safely camelCases a string, taking into account acronyms, kebab-case, snake_case, and sentence casing as well as special characters
 * @param string
 * @returns A `string` in camelCase form
 */
export const sentenceToCamelCase = (string: string): string =>
  string
    .replace(wordSeparators, " ")
    .replace(notAlphaNumericOrSpace, "")
    .replace(capitalizedFirstLetter, ($, ofs) => (ofs ? " " : "") + $.trim())
    .toLowerCase()
    .trim()
    .split(" ")
    .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join("");

/**
 * Check If Empty String
 *
 * this helper function checks if the string is empty
 *
 * if empty it returns undefined, else returns the string
 *
 * useful for form values since we must set a value to empty string if not used
 *
 * @param {String} string
 */
export const checkIfEmptyString = (string: string): string | undefined => {
  if (string === "") {
    return undefined;
  }

  return string;
};

/** Check if a string contains a trademark or registered legal symbol, and remove */
export const removeLegalSymbols = (text: string) =>
  text.replace(/(™|®|&trade;|&reg;|&#8482;|&#174;)/g, "");

/**
 * Remove M5 From Handle
 *
 * Remove the m5- prefix from a handle
 
 * @param handle
 * @returns
 */
export const removeM5FromHandle = (handle: string): string => handle.replace(/^m5-/, "");

/**
 * Sanitize Shopify Handle
 *
 * This function is used to sanitize the handle of a product from Shopify
 *
 * Since we have multiple handles tied to a single product, this function
 * can help us remove the extra information from the handle.
 *
 * This function is used in theme as well as site, which is why it's in strings rather than product-data
 *
 * @param {string} handle
 * @returns {string} sanitized handle
 */
export const sanitizeShopifyHandle = (handle: string) =>
  handle.replace(/(m5-)|(-deal)|(-alum)|(-intl)/g, "");
